import React, {useContext, useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import useDispatchAction from '@hooks/useDispatchAction'
import {getTreatmentPlan} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileTreatmentPlan.slice'
import {safeParseInt} from 'utils/ConstFunctions'
import {useParams} from 'react-router-dom'
import {AuthContext} from 'context/AuthContext'
import PatientCard from './components/PatientCard'
import OrderDetailsCard from './components/OrderDetailsCard'
import ProductionDetailsTable from './components/ProductionDetailsTable'
import FooterDetails from './components/FooterDetails'
import {getLeadsProfileDetails} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileDetails.slice'
import clsx from 'clsx'
import moment from 'moment'
import hasValue from 'utils/hasValue'
import When from 'components/when/When'
import {getMyTaskList} from 'redux/Slices/AppSlice/Profile/Profile.slice'
import STLFilesView from '../ProductionSetup/components/STLFilesView'
import {allFile} from 'redux/Slices/AppSlice/LeadsProfile/LeadsFiles.Slice'
import {ITreatmentPlan} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'
import {getOrderDetails} from 'redux/Slices/AppSlice/orders/orders.slice'
import {Spin} from 'antd'
import Spinner from 'components/spinner/Spinner'
import useAllUserPlan from '@hooks/useAllUserPlan'
import {Pencil} from 'lucide-react'

export type ProductionReviewEditStep = 'setup' | 'batch' | 'stl' | 'shipping' | 'instructions'

export const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = '',
  children,
  ...rest
}) => (
  <div
    className={['bg-white rounded-2xl shadow-sm ring-1 ring-black/5', 'p-5 md:p-6', className].join(
      ' '
    )}
    {...rest}
  >
    {children}
  </div>
)

export const SectionTitle: React.FC<{
  title: string
  className?: string
  onEdit?: () => void
}> = ({title, className = '', onEdit}) => {
  if (!onEdit) {
    return (
      <h3 className={['text-base font-semibold tracking-wide mb-3', className].join(' ')}>
        {title}
      </h3>
    )
  }

  return (
    <div className={['flex items-center justify-between gap-3 mb-3', className].join(' ')}>
      <h3 className='text-base font-semibold tracking-wide'>{title}</h3>
      <button
        type='button'
        onClick={onEdit}
        aria-label={`Edit ${title}`}
        className='inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-primaryColor hover:bg-primaryColor/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryColor'
      >
        <Pencil className='h-3.5 w-3.5' aria-hidden='true' />
        Edit
      </button>
    </div>
  )
}

const ProductionSetupReview: React.FC<{
  onEditStep?: (step: ProductionReviewEditStep) => void
}> = ({onEditStep}) => {
  const {dispatchAction} = useDispatchAction()
  const {userId} = useContext(AuthContext)
  const {patientId} = useParams()
  const {isPractice} = useAllUserPlan()
  const {treatmentPlan, getTreatmentPlanLoading} = useSelector(
    (state: RootState) => state.leadsProfileTreatmentPlanReducer
  )
  const {cardDetails, plansList} = useSelector((state: RootState) => state.kanban)
  const {loadingOrder} = useSelector((state: RootState) => state.orders)
  const {treatmentId} = useParams()
  const allFiles = useSelector((state: RootState) => state.leadFiles)
  const selectedPlan = useMemo(() => {
    const list = (plansList as any)?.plans_list ?? []
    const workflowPlan = list.find((p: any) => String(p.plan_id ?? p.id) === String(treatmentId))
    if (workflowPlan) return workflowPlan
    if (String(treatmentPlan.treatment_plan_id) !== String(treatmentId)) return undefined
    const upper = treatmentPlan.upper_jaw_details
    const lower = treatmentPlan.lower_jaw_details
    const count = (jaw?: {starts_with: number; ends_with: number}) =>
      jaw?.starts_with ? Math.max(0, jaw.ends_with - jaw.starts_with + 1) : 0
    return {
      ...treatmentPlan,
      title: treatmentPlan.treatment_plan_name,
      total_stages: treatmentPlan.total_aligners ?? count(upper) + count(lower),
      upper_aligner_series: upper?.starts_with ? `${upper.starts_with}-${upper.ends_with}` : '0-0',
      lower_aligner_series: lower?.starts_with ? `${lower.starts_with}-${lower.ends_with}` : '0-0',
    }
  }, [plansList, treatmentId, treatmentPlan])

  useEffect(() => {
    if (patientId != null) {
      getLeadsDetails()
    }
  }, [patientId])

  useEffect(() => {
    if (userId != null) {
      dispatchAction(
        getOrderDetails({
          order_id: String(treatmentPlan?.order_id),
          doctor_id: safeParseInt(userId),
        })
      )
    }
  }, [userId])

  useEffect(() => {
    if (treatmentId != null) {
      dispatchAction(
        getTreatmentPlan({
          aligner_treatment_id: String(treatmentId),
        })
      )
    }
  }, [treatmentId])

  useEffect(() => {
    if (treatmentId != null) {
      dispatchAction(
        getTreatmentPlan({
          aligner_treatment_id: String(treatmentId),
        })
      )
        .unwrap()
        .then((res: ITreatmentPlan) => {
          dispatchAction(
            allFile({
              doctor_id: String(userId),
              patient_id: String(patientId),
              path: `/Orders/STL ${res?.treatment_plan_name + res.treatment_plan_id}`,
            })
          )
        })
    }

    dispatchAction(
      getMyTaskList({
        doctor_id: Number(userId),
        patient_id: Number(patientId),
        filter: null,
        order: 'ASC',
        my_task_type: 'PRODUCTION_CHECK_LIST',
      })
    )
  }, [treatmentId])

  const getLeadsDetails = () => {
    const postData = {
      patient_id: safeParseInt(patientId),
      doctor_id: safeParseInt(userId),
    }
    dispatchAction(getLeadsProfileDetails(postData) as any)
  }

  return (
    <Spin indicator={<Spinner loading />} spinning={getTreatmentPlanLoading || loadingOrder}>
      <div className='min-h-screen  pb-[70px] md:pb-0'>
        <div className='mx-auto'>
          <p className='text-textColor mb-4 md:mb-6'>
            Verify all details from previous steps before submission.
          </p>

          <div className='grid grid-cols-1 gap-5'>
            <div
              className={clsx(
                'grid grid-cols-1 gap-5',
                cardDetails?.order_id != null ? 'md:grid-cols-2' : 'md:grid-cols-1'
              )}
            >
              <PatientCard />
              {cardDetails?.order_id != null && (
                <OrderDetailsCard onEdit={onEditStep ? () => onEditStep('setup') : undefined} />
              )}
            </div>

            <Card>
              <SectionTitle title='Finalized Plan Details' />
              <div className='text-sm text-slate-700 grid grid-cols-1 md:grid-cols-2 gap-3'>
                <div>
                  <div className='text-slate-500'>Plan</div>
                  <div className='font-medium'>
                    {selectedPlan?.treatment_plan_tag_name ||
                      selectedPlan?.title ||
                      'Treatment Plan'}{' '}
                    {selectedPlan?.version ? ` ${selectedPlan.version}` : ''}
                  </div>
                </div>
                <div>
                  <div className='text-slate-500'>Created On</div>
                  <div className='font-medium'>
                    {selectedPlan?.created_date || selectedPlan?.created || selectedPlan?.created_at
                      ? moment(
                          selectedPlan?.created_date ||
                            selectedPlan?.created ||
                            selectedPlan?.created_at
                        ).format('DD-MMM-YYYY')
                      : '-'}
                  </div>
                </div>
                <div>
                  <div className='text-slate-500'>Total Aligners</div>
                  <div className='font-medium'>{selectedPlan?.total_stages ?? '-'}</div>
                </div>
                <When isTrue={selectedPlan?.upper_aligner_series != '0-0'}>
                  <div>
                    <div className='text-slate-500'>Upper Aligner Series</div>
                    <div className='font-medium'>{selectedPlan?.upper_aligner_series ?? '-'}</div>
                  </div>
                </When>
                <When isTrue={selectedPlan?.lower_aligner_series != '0-0'}>
                  <div>
                    <div className='text-slate-500'>Lower Aligner Series</div>
                    <div className='font-medium'>{selectedPlan?.lower_aligner_series ?? '-'}</div>
                  </div>
                </When>
              </div>
            </Card>

            <ProductionDetailsTable
              onEditProduct={onEditStep ? () => onEditStep('setup') : undefined}
            />
            {(hasValue(allFiles?.Files) || hasValue(treatmentPlan?.stl_file_metadata?.link)) &&
              !isPractice && (
                <Card>
                  <SectionTitle
                    title='Files'
                    onEdit={onEditStep ? () => onEditStep('stl') : undefined}
                  />
                  <STLFilesView
                    showTitle={false}
                    {...{
                      treatmentPlan: treatmentPlan,
                      stlFileMetaData: allFiles?.Files,
                    }}
                  />
                </Card>
              )}

            <FooterDetails
              onEditInstructions={onEditStep ? () => onEditStep('instructions') : undefined}
              onEditShipping={onEditStep ? () => onEditStep('shipping') : undefined}
            />
          </div>
        </div>
      </div>
    </Spin>
  )
}

export default ProductionSetupReview
